import PageLayout from 'global_elements/Layouts/PageLayout';
import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import ReportCard from 'global_elements/Layouts/Cards/ReportCard';
import { useContext, useEffect, useState, JSX } from 'react';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import './styles.scss';
import ReportsProxy from 'api/reports/reportsProxy';
import { TableauReportCard } from 'interfaces/reports/tableauReport';
import { ActiveFacilityContext } from 'context/activeFacility';
import { NavBarVariant } from 'global_elements/Navigation/NavBar/variants';
import DataListsProxy, { DataListType } from 'api/dataLists/dataListsProxy';
import { ListOption } from 'interfaces/dataLists/listOption';
import SelectableRadioButton from 'global_elements/SelectableRadioButton/SelectableRadioButton';
import { UserContext } from 'context/user';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMediaQuery } from 'react-responsive';
import { Roles } from 'constants/roles';
import Paragraph from 'global_elements/Text/Paragraph';

const CORE_MEASURES_CATEGORY_ID = 5;

export default function ReportListPage(): JSX.Element {
  const { user, selectedReportCategory, SetSelectedReportCategory, isNavExpanded, SetIsNavExpanded } = useContext(UserContext);
  const [rawReportCardData, setRawReportCardData] = useState<TableauReportCard[]>([]);
  const [filteredReportOptions, setFilteredReportOptions] = useState<TableauReportCard[]>([]);
  const [loadingReportOptions, setLoadingReportOptions] = useState<boolean>(true);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [categories, setCategories] = useState<ListOption[]>([]);
  const { facility } = useContext(ActiveFacilityContext);
  const isLaptop = useMediaQuery({
    query: 'only screen and (max-width : 1340px)',
  });
  const isCorpUser = user?.role === Roles.CORPORATION || user?.role === Roles.SUPER_CORPORATION;

  const getNavbarVariant = (): NavBarVariant => {
    if (isLaptop) {
      return NavBarVariant.PRIMARY;
    }
    return isNavExpanded ? NavBarVariant.ICON_EXPANDED : NavBarVariant.ICON;
  };

  const filterReports = (cards: TableauReportCard[], categoryID: number): void => {
    if (categoryID !== 0) {
      // If looking for a specific category, filter card list
      const filteredCardsByCategory = cards.filter((card) => card.tableauCategoryID === categoryID);
      setFilteredReportOptions(filteredCardsByCategory);
    } else {
      // Else, for all reports, need to filter to get unique reports
      const uniqueReports = cards.filter((item, i, ar) => ar.findIndex((x) => x.workbookHash === item.workbookHash) === i);
      setFilteredReportOptions(uniqueReports ?? []);
    }
  };

  useEffect(() => {
    SetIsNavExpanded(false);
    DataListsProxy.getOptionList(
      DataListType.TableauCategory,
      (responseData) => {
        if (responseData.data) {
          setCategories(responseData.data);
        }
        setLoadingCategories(false);
      },
      (errorResponse) => {
        console.log(errorResponse);
        setLoadingCategories(false);
      },
    );
  }, [SetIsNavExpanded]);

  useEffect(() => {
    setLoadingReportOptions(true);
    const facilityId = parseInt(facility.value, 10);

    // Reset category when "All Facilities" is selected
    if (facilityId === 0) {
      SetSelectedReportCategory(0);
    }

    ReportsProxy.getReportCards(facilityId)
      .then((response) => {
        // console.log(response);
        const reportOptions = response.map(
          (report) => ({
            reportRole: report.reportRole,
            workbookDescription: report.workbookDescription,
            workbookHash: report.workbookHash,
            workbookName: report.workbookName,
            tableauCategory: report.tableauCategory,
            tableauCategoryID: report.tableauCategoryID,
            sortOrder: report.sortOrder,
            isCoreMeasures: response.findIndex((x) => x.tableauCategoryID === CORE_MEASURES_CATEGORY_ID && x.workbookHash === report.workbookHash) >= 0,
            programFilter: report.programFilter,
            programFilterID: report.programFilterID,
          }) as TableauReportCard,
        );
        setRawReportCardData(reportOptions);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoadingReportOptions(false);
      });
  }, [facility]);

  useEffect(() => {
    filterReports(rawReportCardData, selectedReportCategory);
  }, [selectedReportCategory, rawReportCardData]);

  const getCategoryBar = (): JSX.Element => (
    <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="category-container">
      <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.END} extraClasses="category-buttons-container">
        {categories.map(
          (category: ListOption): JSX.Element => (
            <SelectableRadioButton
              value={category.lookupID}
              title={category.lookupDesc}
              isSelected={selectedReportCategory === category.lookupID}
              onClick={() => SetSelectedReportCategory(category.lookupID)}
              extraClasses="report-category-button"
              fontSize={FontSizes.SMALL}
            />
          ),
        )}
      </FlexContainer>
    </FlexContainer>
  );

  return (
    <PageLayout layout={PageLayoutVariant.ICON_NAV} testText="Reports Page" navBarVariant={getNavbarVariant()} pageTitle="Reports">
      {(!isCorpUser || facility.value !== '0') && (
        getCategoryBar()
      )}
      <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="reports-page">
        {loadingReportOptions || loadingCategories ? (
          <Paragraph fontColor={FontColors.PRIMARY} fontSize={FontSizes.REGULAR} text="Loading..." testId="report-cards-loading-message" />
        ) : (
          <>
            <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} wrap extraClasses="report-card-container">
              {rawReportCardData.length === 0 ? (
                <Paragraph fontColor={FontColors.PRIMARY} fontSize={FontSizes.REGULAR} text="No report cards to display." testId="no-report-card-message" />
              ) : (
                filteredReportOptions.map((report) => (
                  <ReportCard
                    title={report.workbookName}
                    reportIDHash={report.workbookHash}
                    reportDesc={report.workbookDescription}
                    isCoreMeasures={report.isCoreMeasures}
                    programFilter={report.programFilter}
                    programFilterID={report.programFilterID}
                  />
                ))
              )}
            </FlexContainer>
          </>
        )}
      </FlexContainer>
    </PageLayout>
  );
}
